import { updateStripeConnectAccount } from '../../../services/vault/stripeConnectAccount';
import { StripeMetadataInput, SuccessData } from '../../VaultForm';
import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import { StripeEnv } from '../../../util/enums';
import { createProviderEntities } from './providerEntities';
import { createOrgsEntities } from './orgsEntities';
import logger from '../../../util/logger';
import { ORG_CONFIRMATION_SUBMIT_TREATMENT } from '../../../split';
import { confirmOrg } from '../../../services/vault/orgConfirmation';

export type RepresentativeData = {
  userId: string;
  stripePersonId?: string;
};

export default async function submit({
  stripeEnv,
  kyb,
  representative,
  primaryKycStripePerson,
  submittedOnYourBehalf,
  org,
  financialAccountNode,
  currentUserId,
  statementDescriptor,
  metadata,
  provider,
  splitTreatments,
  bankAccountPayableOrgId,
  fundraiserId,
  fundraiserSlug,
  productDescription,
  owners,
}: {
  stripeEnv: StripeEnv;
  kyb: Kyb;
  representative: Kyc;
  primaryKycStripePerson?: string;
  submittedOnYourBehalf: boolean;
  org: OrgNode;
  financialAccountNode?: FinancialAccountNode;
  currentUserId: string;
  statementDescriptor?: string;
  metadata?: StripeMetadataInput;
  provider: VaultProvider;
  splitTreatments: SplitIO.TreatmentsWithConfig;
  bankAccountPayableOrgId?: string;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
  productDescription?: string | null;
  owners: Owner[];
}): Promise<SuccessData> {
  if (
    splitTreatments[ORG_CONFIRMATION_SUBMIT_TREATMENT.name]?.treatment ===
    ORG_CONFIRMATION_SUBMIT_TREATMENT.treatments.on
  ) {
    if (!financialAccountNode?.acctId) {
      throw new Error('financialAccountNode.acctId required');
    }
    return confirmOrg({
      provider,
      financialAccountNode,
      kyb,
      org,
      representative,
      primaryKycStripePerson,
      currentUserId,
      submittedOnYourBehalf,
      metadata,
      statementDescriptor,
      stripeEnv,
      bankAccountPayableOrgId,
      fundraiserId,
      fundraiserSlug,
      productDescription,
      owners,
    });
  } else {
    if (kyb.id === undefined) {
      throw new Error('Kyb does not have an id');
    }
    if (representative.id === undefined) {
      throw new Error('Representative Kyc does not have an id');
    }
    if (submittedOnYourBehalf) {
      logger(
        "OrgConfirmation submit: data submitted on program's behalf. Creating account and financial account node.",
      );
      const providerEntities = await createProviderEntities({
        provider,
        kybId: kyb.id,
        kycId: representative.id,
        stripeEnv,
        statementDescriptor,
        metadata,
      });
      const financialAccountNode = await createOrgsEntities({
        providerEntities,
        kybId: kyb.id,
        org,
        kycId: representative.id,
        kycIsOwner: representative.owner || false,
        stripeEnv,
        currentUserId,
      });
      return {
        kybData: {
          kybId: kyb.id,
          org,
          submittedOnYourBehalf: true,
          stripeConnectAccountId:
            providerEntities.stripeEntities?.connectAccountId,
          financialAccountId: financialAccountNode.properties.finAcctId,
        },
        representative: {
          vaultKycId: representative.id,
          stripePersonId: providerEntities.stripeEntities?.personId,
        },
      };
    } else {
      if (financialAccountNode === undefined) {
        throw new Error('financialAccountNode not found');
      }
      if (financialAccountNode.acctId === undefined) {
        throw new Error('financialAccountNode is missing acctId');
      }
      if (
        financialAccountNode.finAcctId === undefined ||
        financialAccountNode.finAcctId === null
      ) {
        throw new Error('financialAccountNode has no finAcctId');
      }
      if (statementDescriptor !== undefined || metadata !== undefined) {
        await updateStripeConnectAccount(
          financialAccountNode.acctId,
          kyb.id,
          statementDescriptor,
          metadata,
          stripeEnv,
        );
      }

      logger(
        'OrgConfirmation submit: data represents the program info passed in. User confirmed data.',
      );
      return {
        kybData: {
          kybId: kyb.id,
          org,
          submittedOnYourBehalf: false,
          stripeConnectAccountId: financialAccountNode.acctId,
          financialAccountId: financialAccountNode.finAcctId || undefined,
        },
        representative: {
          vaultKycId: representative.id,
          stripePersonId: primaryKycStripePerson,
        },
      };
    }
  }
}
